import { Fragment, type CSSProperties, type ReactElement } from 'react';
import { CIBLE_MIN, COULEURS, styleBouton, styleBoutonPrincipal } from '../../app/styles.js';
import { clavierFenetre } from './clavier-fenetre.js';
import type { ActionEnAttente, ActionsEquipe, EtatEquipe } from './controleur.js';
import {
  LIBELLES_REGLES,
  LIBELLES_STATUT_LIGNE,
  MAX_ECHEANCES,
  MESSAGE_SANS_REGLES,
  MESSAGE_SANS_SIGNATURE,
  ORDRE_REGLES,
} from './libelles.js';
import { dateFr, echeancesParHeritier, euros } from './modele.js';
import type { Argent, CodeRegle, DonneesEquipe, Fournisseur, JeuRegles } from './types.js';

export interface PropsFacturationEquipe {
  etat: EtatEquipe;
  actions: ActionsEquipe;
}

const styleSection: CSSProperties = {
  border: `1px solid ${COULEURS.bordure}`,
  borderRadius: 8,
  background: COULEURS.blanc,
  padding: 16,
  margin: '16px 0',
};
const styleEtiquette: CSSProperties = { display: 'flex', alignItems: 'center', gap: 12, minHeight: CIBLE_MIN };
const styleChamp: CSSProperties = {
  minHeight: CIBLE_MIN,
  padding: '0 12px',
  font: 'inherit',
  border: `1px solid ${COULEURS.bordure}`,
  borderRadius: 8,
};
const styleCellule: CSSProperties = { padding: '8px 12px', textAlign: 'left', verticalAlign: 'middle' };
const styleDefilement: CSSProperties = { overflowX: 'auto' };
const FOURNISSEURS: readonly { code: Fournisseur; nom: string }[] = [
  { code: 'stripe', nom: 'Stripe' },
  { code: 'gocardless', nom: 'GoCardless' },
];

/** Dossier sans règles (écart C10) : aucune case cochée, la première version ne portera que ce qui est coché. */
const JEU_VIDE: JeuRegles = { versionNo: 0, reason: '', createdAt: '', items: [] };

const sommeCentimes = (a: Argent, b: Argent): Argent => ({ amountCents: a.amountCents + b.amountCents, currency: 'EUR' });

function decrire(action: ActionEnAttente, donnees: DonneesEquipe, etat: EtatEquipe): string[] {
  const nom = (id: string): string => donnees.facturation?.perHeir.find((l) => l.personId === id)?.displayName ?? 'cet héritier';
  switch (action.genre) {
    case 'regles': {
      const modifiees = (Object.keys(etat.brouillon) as CodeRegle[]).map(
        (c) => `${LIBELLES_REGLES[c]?.libelle ?? c} (${etat.brouillon[c] ? 'cochée' : 'décochée'})`,
      );
      return [
        'Une nouvelle version des règles va être créée ; les factures déjà émises ne changent pas.',
        `Règles modifiées : ${modifiees.join(', ')}.`,
        `Raison : ${etat.raison.trim()}.`,
      ];
    }
    case 'facture':
      return [
        `Une facture va être émise pour ${nom(action.personId)}.`,
        'Une fois émise, elle ne pourra plus être modifiée ; une correction passe par un avoir.',
      ];
    case 'echeancier':
      return [
        `Un échéancier de ${action.count} échéance${action.count > 1 ? 's' : ''} va être créé pour ${nom(action.personId)}, sur un reste dû de ${euros(action.total)}.`,
      ];
    case 'liens': {
      const personId = donnees.collecte?.installments.find((e) => action.installmentIds.includes(e.id))?.personId ?? '';
      const fournisseur = FOURNISSEURS.find((f) => f.code === action.provider)?.nom ?? action.provider;
      return [
        `Les liens de paiement de ${nom(personId)} vont être créés avec ${fournisseur} pour ${action.installmentIds.length} échéance(s).`,
      ];
    }
  }
}

const styleChampRemise: CSSProperties = { display: 'flex', flexDirection: 'column', gap: 4 };
// Cibles tactiles de 44 px, étiquettes comprises (T-L57-EQ-27).
const styleEtiquetteRemise: CSSProperties = {
  fontWeight: 600,
  fontSize: 14,
  minHeight: CIBLE_MIN,
  display: 'flex',
  alignItems: 'flex-end',
};
const styleErreur: CSSProperties = { color: COULEURS.rouge, margin: 0 };

/**
 * FACT-22 : montant, motif et héritier visé de la remise, sous sa case, sur le modèle de la carte des suppléments (pas de nouvelle
 * maquette, arbitrage du team-lead du 27/10). Chaque champ porte son propre message d'erreur.
 */
function ChampsRemise({ etat, actions }: PropsFacturationEquipe): ReactElement {
  const heritiers = etat.donnees?.facturation?.perHeir ?? [];
  const { montant: erreurMontant, motif: erreurMotif } = etat.erreursRemise ?? {};
  const saisie = etat.remise ?? { montant: '', motif: '', personId: '' };
  return (
    <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(160px, 1fr))', gap: 12, margin: '0 0 8px 36px' }}>
      <div style={styleChampRemise}>
        <label htmlFor="remise-montant" style={styleEtiquetteRemise}>
          Montant (€)
        </label>
        <input
          id="remise-montant"
          name="remise-montant"
          inputMode="decimal"
          value={saisie.montant}
          disabled={etat.occupe}
          aria-invalid={erreurMontant ? true : undefined}
          aria-describedby={erreurMontant ? 'erreur-remise-montant' : undefined}
          style={styleChamp}
          onChange={(e) => actions.onSaisirRemise('montant', e.target.value)}
        />
        {erreurMontant ? (
          <p id="erreur-remise-montant" role="alert" style={styleErreur}>
            {erreurMontant}
          </p>
        ) : null}
      </div>
      <div style={styleChampRemise}>
        <label htmlFor="remise-motif" style={styleEtiquetteRemise}>
          Motif
        </label>
        <input
          id="remise-motif"
          name="remise-motif"
          maxLength={200}
          value={saisie.motif}
          disabled={etat.occupe}
          aria-invalid={erreurMotif ? true : undefined}
          aria-describedby={erreurMotif ? 'erreur-remise-motif' : undefined}
          style={styleChamp}
          onChange={(e) => actions.onSaisirRemise('motif', e.target.value)}
        />
        {erreurMotif ? (
          <p id="erreur-remise-motif" role="alert" style={styleErreur}>
            {erreurMotif}
          </p>
        ) : null}
      </div>
      <div style={styleChampRemise}>
        <label htmlFor="remise-heritier" style={styleEtiquetteRemise}>
          Héritier visé (facultatif)
        </label>
        <select
          id="remise-heritier"
          name="remise-heritier"
          value={saisie.personId}
          disabled={etat.occupe}
          style={styleChamp}
          onChange={(e) => actions.onSaisirRemise('personId', e.target.value)}
        >
          <option value="">Tous les héritiers</option>
          {heritiers.map((h) => (
            <option key={h.personId} value={h.personId}>
              {h.displayName}
            </option>
          ))}
        </select>
      </div>
    </div>
  );
}

/** Contenu de D08 : règles à cocher, lignes par héritier, calendrier de collecte. Sans état propre (le contrôleur le porte). */
export function FacturationEquipe({ etat, actions }: PropsFacturationEquipe): ReactElement {
  const entete = <h1 style={{ color: COULEURS.marine, fontSize: 28 }}>Facturation du dossier</h1>;
  if (etat.statut !== 'pret' || !etat.donnees) {
    return (
      <section data-composant="facturation-equipe">
        {entete}
        {etat.statut === 'chargement' ? <p role="status">Chargement de la facturation en cours…</p> : null}
        {etat.statut === 'erreur' && etat.message ? <p role="alert">{etat.message}</p> : null}
        {etat.statut === 'sans-selection' && etat.message ? <p role="status">{etat.message}</p> : null}
      </section>
    );
  }
  const { facturation, collecte } = etat.donnees;
  const jeu = facturation?.ruleSet ?? JEU_VIDE;
  const groupes = facturation && collecte ? echeancesParHeritier(collecte, facturation.perHeir) : [];
  const nbBrouillon = Object.keys(etat.brouillon).length;

  return (
    <section data-composant="facturation-equipe">
      {entete}
      {etat.message ? <p role="status">{etat.message}</p> : null}

      <section style={styleSection} aria-labelledby="titre-regles">
        <h2 id="titre-regles">Règles de ce dossier</h2>
        <p>Cochez ou décochez : chaque enregistrement crée une nouvelle version, les factures émises ne bougent pas.</p>
        {facturation ? null : <p>{MESSAGE_SANS_REGLES}</p>}
        {ORDRE_REGLES.map((code) => {
          const enCours = jeu.items.find((i) => i.ruleCode === code)?.enabled ?? false;
          const coche = etat.brouillon[code as CodeRegle] ?? enCours;
          const libelle = LIBELLES_REGLES[code];
          return (
            <Fragment key={code}>
              <label style={styleEtiquette}>
                <input
                  type="checkbox"
                  name={`regle-${code}`}
                  checked={coche}
                  disabled={etat.occupe}
                  style={{ width: 24, height: 24 }}
                  onChange={() => actions.onBasculerRegle(code as CodeRegle)}
                />
                <span>
                  <strong>{libelle?.libelle ?? code}</strong>
                  <br />
                  <span style={{ color: COULEURS.ardoise }}>{libelle?.aide}</span>
                </span>
              </label>
              {code === 'remise' && coche ? ChampsRemise({ etat, actions }) : null}
            </Fragment>
          );
        })}
        {nbBrouillon > 0 ? <p role="status">Modifications non enregistrées : {nbBrouillon}.</p> : null}
        <label style={styleEtiquette}>
          <span>Raison du changement</span>
          <input
            type="text"
            name="raison"
            value={etat.raison}
            disabled={etat.occupe}
            style={{ ...styleChamp, flex: 1 }}
            onChange={(e) => actions.onSaisirRaison(e.target.value)}
          />
        </label>
        <button type="button" style={styleBoutonPrincipal} disabled={etat.occupe} onClick={() => actions.onDemander({ genre: 'regles' })}>
          Enregistrer les règles
        </button>
        {facturation && jeu.items.every((i) => !i.enabled) && nbBrouillon === 0 ? (
          <p>Aucune règle cochée : rien n’est facturé sur ce dossier.</p>
        ) : null}
      </section>

      {facturation ? (
        <>
          <section style={styleSection} aria-labelledby="titre-historique">
            <h2 id="titre-historique">Historique des règles</h2>
            <p>
              Version {jeu.versionNo} en vigueur, enregistrée le {dateFr(jeu.createdAt)} : {jeu.reason}
            </p>
            <p style={{ color: COULEURS.ardoise }}>Seule la version en vigueur est disponible pour le moment.</p>
          </section>

          <section style={styleSection} aria-labelledby="titre-lignes">
            <h2 id="titre-lignes">Lignes par héritier</h2>
            <dl style={{ display: 'flex', gap: 24, flexWrap: 'wrap' }}>
              <div>
                <dt>Facturé</dt>
                <dd>{euros(sommeCentimes(facturation.collected, facturation.outstanding))}</dd>
              </div>
              <div>
                <dt>Encaissé</dt>
                <dd>{euros(facturation.collected)}</dd>
              </div>
              <div>
                <dt>Reste dû</dt>
                <dd>{euros(facturation.outstanding)}</dd>
              </div>
            </dl>
            <label style={styleEtiquette}>
              <span>Nombre d’échéances (1 à {MAX_ECHEANCES})</span>
              <select
                name="nbEcheances"
                value={etat.nbEcheances}
                disabled={etat.occupe}
                style={styleChamp}
                onChange={(e) => actions.onChoisirNombre(Number(e.target.value))}
              >
                {Array.from({ length: MAX_ECHEANCES }, (_, i) => i + 1).map((n) => (
                  <option key={n} value={String(n)}>
                    {n}
                  </option>
                ))}
              </select>
            </label>
            <div style={styleDefilement}>
              <table style={{ borderCollapse: 'collapse', width: '100%' }}>
                <caption style={{ textAlign: 'left' }}>Montants dus, réglés et restant à régler par héritier</caption>
                <thead>
                  <tr>
                    {['Héritier', 'Dû', 'Réglé', 'Reste dû', 'Statut', 'Signature', 'Actions'].map((t) => (
                      <th key={t} scope="col" style={styleCellule}>
                        {t}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {facturation.perHeir.map((l) => (
                    <tr key={l.personId}>
                      <th scope="row" style={styleCellule}>
                        {l.displayName}
                      </th>
                      <td style={styleCellule}>{euros(l.due)}</td>
                      <td style={styleCellule}>{euros(l.paid)}</td>
                      <td style={styleCellule}>{euros(l.remaining)}</td>
                      <td style={styleCellule}>{LIBELLES_STATUT_LIGNE[l.status] ?? l.status}</td>
                      <td style={styleCellule}>{l.blocksSignature ? 'Bloque la signature' : 'Ne bloque pas'}</td>
                      <td style={styleCellule}>
                        {l.remaining.amountCents > 0 ? (
                          <>
                            <button
                              type="button"
                              style={styleBouton}
                              disabled={etat.occupe}
                              aria-label={`Émettre la facture de ${l.displayName}`}
                              onClick={() => actions.onDemander({ genre: 'facture', personId: l.personId })}
                            >
                              Émettre la facture
                            </button>{' '}
                            <button
                              type="button"
                              style={styleBouton}
                              disabled={etat.occupe}
                              aria-label={`Créer l’échéancier de ${l.displayName} en ${etat.nbEcheances} fois`}
                              onClick={() =>
                                actions.onDemander({
                                  genre: 'echeancier',
                                  personId: l.personId,
                                  total: l.remaining,
                                  count: etat.nbEcheances,
                                })
                              }
                            >
                              Créer l’échéancier
                            </button>
                          </>
                        ) : null}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </section>
        </>
      ) : null}

      <section style={styleSection} aria-labelledby="titre-collecte">
        <h2 id="titre-collecte">Calendrier de collecte avant la signature</h2>
        {collecte ? (
          <>
            <p>
              Signature chez le notaire le {dateFr(collecte.signatureDate)}. Marge de {collecte.marginDays} jours avant la signature. Tout
              doit être réuni le {dateFr(collecte.deadline)}.
            </p>
            {groupes.length === 0 ? <p>Aucune échéance n’est encore planifiée.</p> : null}
            {groupes.map((g) => {
              const aRegler = g.echeances.filter((e) => e.aRegler).map((e) => e.id);
              return (
                <div key={g.personId} style={{ margin: '12px 0' }}>
                  <h3>{g.nom}</h3>
                  <ul>
                    {g.echeances.map((e) => (
                      <li key={e.id}>
                        {e.date} · {e.montant} · {e.statut}
                        {e.manuel ? ' · Modifié à la main' : ''}
                      </li>
                    ))}
                  </ul>
                  {aRegler.length > 0
                    ? FOURNISSEURS.map((f) => (
                        <button
                          key={f.code}
                          type="button"
                          style={styleBouton}
                          disabled={etat.occupe}
                          aria-label={`Envoyer le lien de paiement de ${g.nom} par ${f.nom}`}
                          onClick={() => actions.onDemander({ genre: 'liens', installmentIds: aRegler, provider: f.code })}
                        >
                          Lien {f.nom}
                        </button>
                      ))
                    : null}
                </div>
              );
            })}
            <p>
              Les frais d’acte sont payés directement au notaire : le Cabinet MAAT ne les encaisse pas. Le calendrier rappelle à chacun ce
              qu’il doit verser à l’étude avant la signature.
            </p>
          </>
        ) : (
          <>
            <p>{MESSAGE_SANS_SIGNATURE}</p>
            <button type="button" style={styleBouton} disabled={etat.occupe} onClick={() => actions.onReessayer()}>
              Réessayer
            </button>
          </>
        )}
      </section>

      {etat.enAttente ? (
        <div
          role="alertdialog"
          aria-modal="true"
          aria-labelledby="titre-confirmation"
          onKeyDown={(e) => clavierFenetre(e, actions.onAnnuler, etat.occupe)}
          style={{ ...styleSection, borderColor: COULEURS.ambre, background: COULEURS.ambreFond }}
        >
          <h2 id="titre-confirmation">Confirmez-vous cette action ?</h2>
          {decrire(etat.enAttente, etat.donnees, etat).map((l) => (
            <p key={l}>{l}</p>
          ))}
          {/* Le focus entre dans la fenêtre à l'ouverture (écart C18). */}
          <button type="button" style={styleBoutonPrincipal} disabled={etat.occupe} autoFocus onClick={() => actions.onConfirmer()}>
            Confirmer
          </button>{' '}
          <button type="button" style={styleBouton} onClick={() => actions.onAnnuler()}>
            Annuler
          </button>
        </div>
      ) : null}
    </section>
  );
}
